import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import React, { Fragment } from 'react';

export default function ColorSchemeSelect({
    value,
    onChange,
    groups = [],
    swatchFn = () => 'bg-neutral-200',
    renderSwatch,
}) {
    const flat = Array.isArray(groups)
        ? groups.flatMap((g) => (Array.isArray(g?.options) ? g.options : []))
        : [];

    const values = flat.map((o) => o.value);
    const normalized = values.includes(value) ? value : values[0];

    React.useEffect(() => {
        if (flat.length === 0) return;
        if (value !== normalized) onChange?.(normalized);
    }, [flat.length, value, normalized, onChange]);

    if (flat.length === 0) return null;

    return (
        <div className="rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
            <Listbox value={normalized} onChange={onChange}>
                <div className="relative">
                    <Listbox.Button className="relative flex h-9 w-full cursor-default items-center rounded-[10px] bg-white px-3 pr-8 text-left text-xs leading-5 text-neutral-800 focus:outline-none md:text-sm">
                        <span>
                            {flat.find((o) => o.value === normalized)?.label ??
                                ''}
                        </span>
                        <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                            <ChevronDownIcon className="h-4 w-4 text-neutral-500" />
                        </span>
                    </Listbox.Button>

                    <Transition
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-95"
                    >
                        <Listbox.Options className="absolute z-50 mt-1 max-h-72 w-full overflow-auto rounded-lg border border-neutral-200 bg-white p-1 text-xs shadow-lg ring-1 ring-black/5 focus:outline-none">
                            {groups.map((group) => (
                                <div
                                    key={group.label ?? JSON.stringify(group)}
                                    className="py-1"
                                >
                                    <div className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-neutral-400">
                                        {group.label}
                                    </div>

                                    {(group.options ?? []).map((option) => (
                                        <Listbox.Option
                                            key={option.value}
                                            value={option.value}
                                            className={({ active, selected }) =>
                                                [
                                                    'flex cursor-pointer items-center justify-between rounded-md px-2 py-1.5',
                                                    active
                                                        ? 'bg-primary-50 text-primary-700'
                                                        : 'text-neutral-800',
                                                    selected
                                                        ? 'font-medium'
                                                        : '',
                                                ].join(' ')
                                            }
                                        >
                                            {({ selected }) => (
                                                <>
                                                    <span className="flex min-w-0 items-center gap-2">
                                                        {renderSwatch ? (
                                                            renderSwatch(option)
                                                        ) : (
                                                            <span
                                                                aria-hidden="true"
                                                                className={`h-2.5 w-2.5 shrink-0 rounded-full opacity-90 ring-1 ring-black/5 ${swatchFn(
                                                                    option.value
                                                                )}`}
                                                            />
                                                        )}
                                                        <span className="truncate">
                                                            {option.label}
                                                        </span>
                                                    </span>
                                                    {selected && (
                                                        <CheckIcon className="h-4 w-4 text-primary-600" />
                                                    )}
                                                </>
                                            )}
                                        </Listbox.Option>
                                    ))}
                                </div>
                            ))}
                        </Listbox.Options>
                    </Transition>
                </div>
            </Listbox>
        </div>
    );
}
